import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { updateLastActivity } from '../../Utils/LastActivity';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { JSX } from 'react';
import type ChecklistModel from '../../Models/Checklists/ChecklistModelModel';
import type { SelectChangeEvent} from '@mui/material';
import { FormControl, FormControlLabel, InputLabel, MenuItem, Radio, RadioGroup, Select } from '@mui/material';
import { ErrorMessage, QuickTextInput } from 'shouldve-been-mui';
import type MemoTemplate from '../../Models/MemoTemplateModel';
import type MemoTemplateTab from '../../Models/MemoTemplateTabModel';
import { shakeForm } from 'shouldve-been-react';
import type ObservationModel from '../../Models/ObservationModelModel';

type AddFolderDialogProps = {
    controller: boolean,
    onClose: (shouldAdd: boolean, folderType: string, templateId: string | null, name: string) => void,
    memoTemplates: MemoTemplate[],
    checklistModels: ChecklistModel[],
    observationModels: ObservationModel[],
};

function AddFolderDialog(props: Readonly<AddFolderDialogProps>): JSX.Element {

    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);

    const formRef: React.RefObject<HTMLFormElement> = useRef<HTMLFormElement>(null);
    
    const [folderType, setFolderType] = useState<string>('blank');
    const [templateId, setTemplateId] = useState<string>('');
    const [name, setName] = useState<string>('');

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    const selectMenuItems: JSX.Element[] = useMemo<JSX.Element[]>(() => {

        const filteredTemplates: MemoTemplate[] = props.memoTemplates.filter((memoTemplate: MemoTemplate) => {

            if (memoTemplate.status !== '-') {
                return false;
            }

            const checklistTab: MemoTemplateTab | null = memoTemplate.tabs.find((tab: MemoTemplateTab) => {
                return props.checklistModels.find((checklistModel: ChecklistModel) => checklistModel.checklistId === tab.tabId);
            }) ?? null;

            const observationTab: MemoTemplateTab | null = memoTemplate.tabs.find((tab: MemoTemplateTab) => {
                return props.observationModels.find((observationModel: ObservationModel) => observationModel.observationId === tab.tabId);
            }) ?? null;

            return checklistTab !== null || observationTab !== null; //only show the templates containing at least one checklist or one observation
        });

        const sortedTemplates: MemoTemplate[] = [...filteredTemplates].sort((a: MemoTemplate, b: MemoTemplate) => a.name.localeCompare(b.name));

        return (
            [
                <MenuItem 
                    key='__empty__'
                    value=''
                >
                    &nbsp;
                </MenuItem>,
            ].concat(sortedTemplates.map((currentTemplate: MemoTemplate) => {
                return (
                    <MenuItem
                        key={currentTemplate.templateId}
                        value={currentTemplate.templateId}
                    >
                        {currentTemplate.name}
                    </MenuItem>
                );
            }))
        );
    }, [props.checklistModels, props.memoTemplates, props.observationModels]);

    function handleClose(shouldAdd: boolean): void {

        updateLastActivity(userContext, localStorageContext);

        if (shouldAdd) { //validate

            if (folderType === 'template' && templateId === '') {
                setErrorMessage('No template selected');
                shakeForm(formRef);
                return;
            }

            if (name === '') {
                setErrorMessage('Name cannot be empty');
                shakeForm(formRef);
                return;
            }
        }

        setFolderType('blank');
        setTemplateId('');
        setName('');

        props.onClose(shouldAdd, folderType, templateId, name);
    }

    return (
        <Dialog 
            open={props.controller} 
            onClose={(): void => handleClose(false)}
            PaperProps={{
                sx: {
                    position: 'fixed',
                },
            }}
        >
            <DialogTitle>Create Folder</DialogTitle>
            <DialogContent>
                <form noValidate autoComplete='off' ref={formRef} name='createFolder'>
                    <RadioGroup
                        value={folderType}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setFolderType(e.target.value)}
                    >
                        <FormControlLabel
                            value='blank'
                            control={
                                <Radio />
                            }
                            label='Blank'
                        />
                        <FormControlLabel
                            value='template'
                            control={
                                <Radio />
                            }
                            label='From template'
                        />
                    </RadioGroup>
                    <br />
                    <FormControl 
                        size='small'
                        style={{
                            width: Math.min(windowSize[0] * 0.8, 500),
                        }}
                    >
                        <InputLabel>Template</InputLabel>
                        <Select
                            value={templateId}
                            onChange={(e: SelectChangeEvent): void => setTemplateId((e.target as HTMLInputElement).value)}
                            size='small'
                            variant='outlined'
                            label='Template'
                            disabled={folderType !== 'template'}
                        >
                            {selectMenuItems}
                        </Select>
                    </FormControl>
                    <br />
                    <br />
                    <QuickTextInput
                        value={name}
                        onBlur={setName}
                        variant='outlined'
                        dense={true}
                        minWidth={Math.min(windowSize[0] * 0.8, 500)}
                        maxWidth={Math.min(windowSize[0] * 0.8, 500)}
                        labelText='Name'
                        height='40px'
                    />
                </form>
                <ErrorMessage
                    message={errorMessage}
                    maxWidth={Math.min(windowSize[0] * 0.8, 500)}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={(): void => handleClose(false)}
                    color='error'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '32px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={(): void => handleClose(true)}
                    color='primary'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '32px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AddFolderDialog;